import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../shared/Logo';

export default function CGUPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <header className="border-b border-slate-800 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={50} />
            <span className="font-bold text-sm text-white">
              Revert<span className="text-amber-500">AI</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
            ← Retour
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-white mb-2">Conditions Générales d'Utilisation</h1>
        <p className="text-slate-500 text-sm mb-10">Dernière mise à jour : avril 2025</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Présentation du service</h2>
            <p>
              RevertAI est une plateforme SaaS de recrutement augmentée par intelligence
              artificielle, éditée par RevertAI (ci-après « l'Éditeur »), joignable à l'adresse{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
              .
            </p>
            <p className="mt-2">
              Le service permet aux professionnels du recrutement d'analyser des CV, de gérer un
              vivier de talents, de créer des missions et d'utiliser des outils de matching basés
              sur l'IA.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Acceptation des conditions</h2>
            <p>
              L'accès et l'utilisation du service impliquent l'acceptation pleine et entière des
              présentes CGU. Si vous n'acceptez pas ces conditions, vous devez cesser d'utiliser le
              service immédiatement.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Accès au service</h2>
            <p>
              Le service est accessible aux professionnels disposant d'un compte valide.
              L'inscription est soumise à validation et peut être limitée selon les offres
              disponibles.
            </p>
            <p className="mt-2">
              L'utilisateur s'engage à fournir des informations exactes lors de son inscription et à
              maintenir la confidentialité de ses identifiants de connexion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Utilisation du service</h2>
            <p>L'utilisateur s'engage à :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
              <li>Utiliser le service dans le cadre de son activité professionnelle légitime.</li>
              <li>
                Ne pas importer de données de candidats sans base légale (consentement, intérêt
                légitime, etc.).
              </li>
              <li>Respecter la réglementation applicable, notamment le RGPD.</li>
              <li>Ne pas tenter de contourner les mesures de sécurité de la plateforme.</li>
              <li>Ne pas utiliser le service à des fins illicites ou préjudiciables.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Données des candidats</h2>
            <p>
              L'utilisateur est responsable en tant que responsable de traitement des données
              personnelles des candidats qu'il importe sur la plateforme. L'Éditeur agit en qualité
              de sous-traitant au sens du RGPD.
            </p>
            <p className="mt-2">
              L'utilisateur garantit disposer des bases légales nécessaires pour traiter les données
              des candidats et s'engage à respecter les droits de ces derniers (accès,
              rectification, effacement, etc.).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Propriété intellectuelle</h2>
            <p>
              L'ensemble des éléments composant la plateforme (interface, algorithmes, contenus)
              sont la propriété exclusive de l'Éditeur et sont protégés par le droit de la propriété
              intellectuelle.
            </p>
            <p className="mt-2">
              L'utilisateur dispose uniquement d'un droit d'usage personnel, non exclusif et non
              transférable du service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">
              7. Disponibilité et évolution du service
            </h2>
            <p>
              L'Éditeur s'efforce d'assurer la disponibilité du service mais ne garantit pas une
              disponibilité ininterrompue. Des interruptions pour maintenance peuvent survenir.
            </p>
            <p className="mt-2">
              L'Éditeur se réserve le droit de faire évoluer les fonctionnalités du service et les
              présentes CGU à tout moment. Les utilisateurs seront informés des modifications
              substantielles.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">
              8. Limitation de responsabilité
            </h2>
            <p>
              Le service est fourni « en l'état ». L'Éditeur ne saurait être tenu responsable des
              dommages indirects résultant de l'utilisation du service, ni de l'exactitude des
              analyses générées par l'IA.
            </p>
            <p className="mt-2">
              Les résultats produits par les outils d'intelligence artificielle sont indicatifs et
              ne constituent pas des décisions de recrutement. La décision finale appartient
              toujours à l'utilisateur.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Résiliation</h2>
            <p>
              L'utilisateur peut résilier son compte à tout moment en contactant l'Éditeur à{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
              .
            </p>
            <p className="mt-2">
              L'Éditeur se réserve le droit de suspendre ou supprimer un compte en cas de violation
              des présentes CGU.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Droit applicable</h2>
            <p>
              Les présentes CGU sont soumises au droit français. En cas de litige, les parties
              s'engagent à rechercher une solution amiable avant tout recours judiciaire. À défaut,
              les tribunaux compétents de Paris seront saisis.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">11. Contact</h2>
            <p>
              Pour toute question relative aux présentes CGU :{' '}
              <a href="mailto:contact@revertai.io" className="text-amber-500 hover:underline">
                contact@revertai.io
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2025 RevertAI. Tous droits réservés.</p>
          <div className="flex gap-6">
            <Link to="/confidentialite" className="hover:text-slate-300 transition-colors">
              Confidentialité
            </Link>
            <a href="mailto:contact@revertai.io" className="hover:text-slate-300 transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
